/**
 * lib/vat.ts
 * ------------------------------------------------------------------
 * VALUE ADDED TAX — off until the school says otherwise.
 *
 * WHY THIS IS A SWITCH AND NOT A FEATURE. Under Botswana's Value Added
 * Tax Act, the supply of EDUCATION IS AN EXEMPT SUPPLY. A school whose
 * only income is tuition therefore does not register, does not charge
 * output tax, and — the part that catches people out — CANNOT RECLAIM
 * THE INPUT TAX it pays its suppliers. That VAT is simply part of the
 * cost of what it buys.
 *
 * So a school like this is usually in one of three positions:
 *
 *   NOT REGISTERED       everything exempt; VAT is a cost, never a
 *                        ledger account. This is the default, and most
 *                        of the time it is correct.
 *   REGISTERED, MIXED    tuition exempt, but something else is taxable —
 *                        a uniform shop, hiring out the hall, a bus
 *                        service. Output tax on those; input tax only on
 *                        what relates to them, apportioned.
 *   REGISTERED, FULLY    unusual for a school, and included because a
 *                        module that cannot express it would be quietly
 *                        wrong for the one that is.
 *
 * TURNING IT ON IS A DECLARATION, NOT A PREFERENCE. Registration is a
 * legal status with a number, an effective date and filing obligations,
 * so the switch asks for those rather than being a tick box — a system
 * that lets somebody flip VAT on and start charging it without a
 * registration number is a system that helps a school commit an offence.
 *
 * THE NUMBERS COME FROM THE LEDGER, NEVER FROM A SEPARATE TALLY. Output
 * and input tax are balances on their own accounts, so the return and
 * the trial balance cannot disagree. A VAT figure kept alongside the
 * books is a VAT figure that drifts from them.
 *
 * PURE. Settings and journals in, a return out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { GlAccount, GlJournal } from '@/lib/gl';

/**
 * Botswana's standard rate is 14%.
 *
 * It was temporarily cut to 12% between 1 August 2022 and 31 March 2023
 * and restored afterwards, which is exactly why the rate is stored on the
 * settings rather than written into the code: a rate constant is a rate
 * that needs a deploy to change, and a school filing a return during a
 * change would file the wrong one.
 */
export const BW_STANDARD_VAT_RATE = 14;

export type VatStatus = 'not_registered' | 'registered';

/** How much of the school's activity is taxable — it decides input tax. */
export type VatBasis = 'fully_taxable' | 'mixed';

export interface VatSettings {
  status: VatStatus;
  /** The BURS registration number. Required to charge tax at all. */
  vatNumber?: string;
  /** When registration took effect. Nothing before it belongs on a return. */
  registeredFrom?: string;
  rate: number;
  basis: VatBasis;
  /**
   * For a mixed school: the proportion of input tax that may be
   * reclaimed, because input tax on exempt activity cannot be. Expressed
   * as a percentage and reviewed annually — it is an apportionment the
   * school agrees with BURS, not something this system can derive.
   */
  recoverablePct?: number;
  /** How often a return is due. Botswana is bi-monthly for most. */
  periodMonths?: 1 | 2 | 6;
}

export const VAT_OFF: VatSettings = {
  status: 'not_registered',
  rate: BW_STANDARD_VAT_RATE,
  basis: 'mixed',
};

/** Is the school actually able to charge and reclaim tax right now? */
export function vatActive(s: VatSettings | undefined | null): boolean {
  return Boolean(s && s.status === 'registered' && (s.vatNumber ?? '').trim());
}

/**
 * Why VAT cannot be switched on yet, or null.
 *
 * Refused rather than warned about: charging tax without a registration
 * number is not a configuration mistake, it is an offence, and a warning
 * is something people click past.
 */
export function vatSetupProblem(s: VatSettings): string | null {
  if (s.status !== 'registered') return null;
  if (!(s.vatNumber ?? '').trim()) {
    return 'A BURS VAT registration number is required before tax can be charged.';
  }
  if (!(s.registeredFrom ?? '').trim()) {
    return 'Give the date registration took effect — nothing before it belongs on a return.';
  }
  if (!(Number(s.rate) > 0)) return 'Set the VAT rate.';
  if (s.basis === 'mixed') {
    const pct = Number(s.recoverablePct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      return 'For a mixed school, set the percentage of input tax that may be reclaimed. '
        + 'Education is an exempt supply, so input tax relating to it cannot be recovered.';
    }
  }
  return null;
}

/* ============================== the maths ============================== */

/**
 * Tax on a VAT-INCLUSIVE amount.
 *
 * A price quoted to a parent or shown on a shelf includes the tax, so
 * extracting it is the common direction and getting it backwards
 * overstates the tax by the rate squared. 14% of 114 is not the tax in
 * 114; 14/114 of it is.
 */
export function vatFromInclusive(gross: number, rate: number): number {
  const r = Number(rate) || 0;
  if (r <= 0) return 0;
  return round2((Number(gross) || 0) * (r / (100 + r)));
}

/** Tax ADDED to a net amount. */
export function vatOnExclusive(net: number, rate: number): number {
  const r = Number(rate) || 0;
  if (r <= 0) return 0;
  return round2((Number(net) || 0) * (r / 100));
}

export const netFromInclusive = (gross: number, rate: number): number =>
  round2((Number(gross) || 0) - vatFromInclusive(gross, rate));

export interface VatReturn {
  from: string;
  to: string;
  /** Tax charged on what the school sold. */
  outputTax: number;
  /** Tax paid to suppliers, before any restriction. */
  inputTaxPaid: number;
  /** What may actually be reclaimed after apportionment. */
  inputTaxRecoverable: number;
  /** Input tax that cannot be reclaimed, and is therefore a cost. */
  inputTaxIrrecoverable: number;
  /** Positive is payable to BURS; negative is a refund due. */
  net: number;
  /** Whether anything in the window predates registration. */
  hasPreRegistration: boolean;
}

/**
 * Build a return from the ledger.
 *
 * OUTPUT AND INPUT TAX ARE LEDGER BALANCES. Output tax sits as a credit
 * on the VAT-output account and input tax as a debit on the VAT-input
 * one, so this reads the journals rather than recomputing tax from
 * invoices. Recomputing would produce a second answer, and two answers to
 * "what do we owe BURS" is the failure this avoids.
 */
export function buildVatReturn(
  settings: VatSettings,
  journals: GlJournal[],
  accounts: GlAccount[],
  from: string,
  to: string,
): VatReturn {
  const codeFor = (key: string) => accounts.find((a) => a.systemKey === key)?.code;
  const outputCode = codeFor('VAT_OUTPUT');
  const inputCode = codeFor('VAT_INPUT');

  let outputTax = 0;
  let inputTaxPaid = 0;
  let hasPreRegistration = false;

  const regFrom = settings.registeredFrom ?? '';

  for (const j of journals) {
    const date = (j.date ?? '').slice(0, 10);
    if (date < from || date > to) continue;

    for (const l of j.lines ?? []) {
      if (outputCode && l.accountCode === outputCode) {
        /* Output tax is a liability: a credit increases it, and a debit
           reduces it when a credit note unwinds a sale. */
        outputTax = round2(outputTax + (Number(l.cr) || 0) - (Number(l.dr) || 0));
        if (regFrom && date < regFrom) hasPreRegistration = true;
      }
      if (inputCode && l.accountCode === inputCode) {
        inputTaxPaid = round2(inputTaxPaid + (Number(l.dr) || 0) - (Number(l.cr) || 0));
        if (regFrom && date < regFrom) hasPreRegistration = true;
      }
    }
  }

  /* Education is exempt, so a mixed school reclaims only the agreed
     proportion. The rest is not lost paperwork — it is a cost, and
     naming it keeps that visible rather than looking like a shortfall. */
  const pct = settings.basis === 'mixed'
    ? Math.min(100, Math.max(0, Number(settings.recoverablePct) || 0))
    : 100;

  const inputTaxRecoverable = round2(inputTaxPaid * (pct / 100));
  const inputTaxIrrecoverable = round2(inputTaxPaid - inputTaxRecoverable);

  return {
    from,
    to,
    outputTax,
    inputTaxPaid,
    inputTaxRecoverable,
    inputTaxIrrecoverable,
    net: round2(outputTax - inputTaxRecoverable),
    hasPreRegistration,
  };
}

/**
 * The periods a return is due for, most recent first.
 *
 * Botswana files bi-monthly for most registrants, so the default walks in
 * two-month steps from the date registration took effect — a school
 * cannot file for a period it was not registered in.
 */
export function vatPeriods(
  settings: VatSettings,
  asOf: Date = new Date(),
  count = 6,
): { from: string; to: string; label: string }[] {
  const step = settings.periodMonths ?? 2;
  const out: { from: string; to: string; label: string }[] = [];

  const end = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() + 1, 0));
  const regFrom = settings.registeredFrom ? new Date(settings.registeredFrom) : null;

  for (let i = 0; i < count; i += 1) {
    const to = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - (i * step) + 1, 0));
    const from = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() - (step - 1), 1));
    if (regFrom && to < regFrom) break;
    out.push({
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
      label: `${from.toISOString().slice(0, 7)} to ${to.toISOString().slice(0, 7)}`,
    });
  }
  return out;
}
